import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Button from '../../../components/Buttons/Button';
import DataTable from '../../../components/DataTable/DataTable';
import ButtonBar from '../../../components/Fields/ButtonBar';
import MultiSelectField from '../../../components/Fields/MultiSelectField';
import SelectListField from '../../../components/Fields/SelectListField';
import { FieldConfig, FormConfig } from '../../../components/Fields/Types';
import useSelectListOptions from '../../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../../components/Spinner/SpinnerOverlay';
import { useNotifications } from '../../../contexts/notificationContext';
import WorkflowTaskModel from '../../../models/WorkflowTaskModel';
import DataService from "../../../services/data.service";
import { Result } from '../../../types/Result';

interface Props {
    fromEmployeeId: number
    toEmployeeId: number
}

interface EmployeeMaintenanceTaskModel {
    fromEmployeeId: number
    toEmployeeId: number
    functionalRoleIds: number[]
    productCategoryId?: number
    brandId?: number
    businessPartnerId?: number
    projectId?: number
    workflowTaskIds: number[]
}

const EmployeeMaintenance_tab_Brands: React.FC<Props> = ({ fromEmployeeId, toEmployeeId }) => {
    const [showSpinner, setShowSpinner] = useState(false)
    const [tableData, setTableData] = useState<WorkflowTaskModel[]>([]);
    const [formData, setFormData] = useState<EmployeeMaintenanceTaskModel>({ fromEmployeeId: fromEmployeeId, toEmployeeId: toEmployeeId, functionalRoleIds: [], workflowTaskIds: [] })
    const { handleResult, handleException } = useNotifications();

    const functionalRoles = useSelectListOptions(`api/v1/selectlist/FunctionalRolesForEmployeeMaintenanceTasks?fromEmployeeId=${fromEmployeeId}&toEmployeeId=${toEmployeeId}`, '-- Select workflow role');
    const productCategories = useSelectListOptions(`api/v1/selectlist/ProductCategoriesForEmployeeMaintenance?fromEmployeeId=${fromEmployeeId}&toEmployeeId=${toEmployeeId}`, '-- All categories');
    const brands = useSelectListOptions(`api/v1/selectlist/BrandsForEmployeeMaintenance?fromEmployeeId=${fromEmployeeId}&toEmployeeId=${toEmployeeId}`, '-- All brands');
    const businessPartners = useSelectListOptions(`api/v1/selectlist/BusinessPartnersForEmployeeMaintenance?fromEmployeeId=${fromEmployeeId}&toEmployeeId=${toEmployeeId}`, '-- All business-partners');
    const projects = useSelectListOptions(`api/v1/selectlist/ProjectsForEmployeeMaintenanceTasks?fromEmployeeId=${fromEmployeeId}&toEmployeeId=${toEmployeeId}`, '-- All projects');

    useEffect(() => {
        formData.fromEmployeeId = fromEmployeeId
        formData.toEmployeeId = toEmployeeId
        setFormData(x => ({ ...formData }));
        getTableData(formData);
    }, [fromEmployeeId, toEmployeeId, formData.functionalRoleIds, formData.productCategoryId, formData.brandId, formData.businessPartnerId, formData.projectId]);

    function getTableData(form) {
        if (fromEmployeeId && toEmployeeId) {
            setShowSpinner(true)
            DataService.post<WorkflowTaskModel[]>(`api/v1/WorkflowTasks/ForEmployeeMaintenance`, form)
                .then(response => {
                    setShowSpinner(false)
                    setTableData(response)
                })
                .catch(e => {
                    setShowSpinner(false)
                    handleException(e);
                });
        }
    }

    function submit() {
        if (tableData) {
            formData.workflowTaskIds = tableData.filter(x => x['_checkbox_selected'] === true).map(x => x.id)
            setShowSpinner(true)
            DataService.post<Result>(`api/v1/WorkflowTasks/ChangeTaskEmployees`, formData)
                .then(response => {
                    setShowSpinner(false)
                    handleResult(response)
                })
                .catch(e => {
                    setShowSpinner(false)
                    handleException(e)
                });
        }
    }

    function setFieldValue(name: string, value: string) {
        if (formData) {
            name.split('.').reduce((object, propertyName, index, elements) => {
                if (index === elements.length - 1) {
                    object[propertyName] = value;
                    setFormData(formData);
                }

                if (object && object[propertyName]) {
                    return object[propertyName];
                }
                return null;
            }, formData);
        }
    }

    function onCheckboxButtonClick(selectedRows: WorkflowTaskModel[]) {
        if (tableData) {
            const select = tableData.length !== selectedRows.length
            tableData.forEach(x => x['_checkbox_selected'] = select);
            setTableData([...tableData])
        }
    }

    let fields: FieldConfig[] = [
        { name: 'functionalRoleIds', label: 'Workflow roles', labelSize: 3, validationRules: [{ type: 'isRequired' }] },
        { name: 'productCategoryId', label: 'Category', labelSize: 3 },
        { name: 'brandId', label: 'Brand', labelSize: 3 },
        { name: 'businessPartnerId', label: 'Business-partner', labelSize: 3 },
        { name: 'projectId', label: 'Project', labelSize: 3 },
    ];

    const formConfig: FormConfig = {
        fields: fields,
        handleSubmit: submit,
        handleFieldChanged: setFieldValue,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    return (
        <>
            <Container fluid>
                <Row>
                    <Col md='6'>
                        <MultiSelectField {...getFieldProps('functionalRoleIds')} options={functionalRoles} />
                        <SelectListField {...getFieldProps('productCategoryId')} options={productCategories} />
                        <SelectListField {...getFieldProps('brandId')} options={brands} />
                        <SelectListField {...getFieldProps('businessPartnerId')} options={businessPartners} />
                        <SelectListField {...getFieldProps('projectId')} options={projects} />
                    </Col>
                </Row>
                <Row>
                    <div style={{ marginTop: 15 }}>
                        <DataTable
                            name='dtEmployeeMaintenanceTasks'
                            tableData={tableData}
                            colInfos={[
                                { key: true, dataProp: 'id', visible: false },
                                { checkboxButton: { text: 'selected', icon: 'fa-check', color: 'green', onClick: onCheckboxButtonClick } },
                                { title: 'User', dataProp: 'assignedToUser', dataType: 'user', sort: 'asc' },
                                { title: 'Role', dataProp: 'assignToFunctionalRole' },
                                { title: 'Task', dataProp: 'task' },
                                { title: 'Status', dataProp: 'taskStatus' },
                                { dataType: 'image', dataProp: 'itemRevision.thumbnailUrl' },
                                { title: 'Item Nr', dataProp: 'itemRevision.item.itemNr' },
                                { dataType: 'image', dataProp: 'project.thumbnailUrl' },
                                { title: 'Project Nr', dataProp: 'project.id' },
                                { title: 'Projectname', dataProp: 'project.name' },
                            ]}
                        />
                    </div>
                </Row>
                {showSpinner && <SpinnerOverlay />}
            </Container>
            <ButtonBar offset={1}>
                <Button type='button' text='Change Employee' onClick={submit} />
            </ButtonBar>
        </>
    );
}
export default EmployeeMaintenance_tab_Brands;